import { defineFieldConfigMap } from "@/components/shared/UniversalCRMTable/types/field-config-types";

/**
 * Core task fields — identity, description, and relations.
 *
 * Each field is self-contained: opening one entry shows its type, display
 * config, GraphQL hint (`graphqlSelector`), and the saved views that include
 * it by default (`savedViewMembership`). Saved-view definitions in
 * `../task-saved-views.ts` derive `visibleColumns` from these declarations
 * via `collectFieldsForSavedView`.
 */
export const TASK_CORE_FIELDS = defineFieldConfigMap({
  // FIELD: template
  template: {
    type: "template-select",
    label: "Template",
    placeholder: "Select a task template...",
    formOnly: true,
    formSpan: 3,
    templateSelectConfig: {
      templateType: "task",
      showClearOption: true,
      fieldMappings: {
        title: "title",
        description: "description",
        tags: "tags",
        priority: "priority",
        status: "status",
      },
    },
    excludeFromApi: true,
    editable: true,
    hideable: true,
    resizable: true,
    width: 260,
    mobileDetailSectionId: "setup",
  },
  // FIELD: title
  title: {
    type: "text",
    label: "Task Title",
    placeholder: "What needs to be done?",
    required: true,
    formSpan: 3,
    validation: {
      required: "Task title is required.",
    },
    sortable: true,
    filterable: true,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: true,
    filterType: "contains",
    width: 300,
    minWidth: 200,
    maxWidth: 500,
    icon: "CheckSquare",
    allowedRoles: ["wtc_employee", "wtc_admin"],
    showRowActions: true,
    showOnCard: true,
    cardSpan: 2,
    mobileDetailSectionId: "basic",
    graphqlSelector: "title",
    savedViewMembership: [
      "all-tasks",
      "my-tasks",
      "high-priority",
      "overdue-tasks",
      "pending-tasks",
      "in-progress-tasks",
      "completed-tasks",
      "cancelled-tasks",
    ],
  },
  // FIELD: description
  description: {
    type: "textarea",
    label: "Description",
    placeholder: "Describe the task requirements...",
    formSpan: 3,
    sortable: false,
    filterable: false,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: true,
    width: 250,
    minWidth: 150,
    maxWidth: 400,
    icon: "FileText",
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "basic",
  },
  // FIELD: contact_id
  contact_id: {
    type: "client-select",
    label: "Contact",
    isForeignKey: true,
    foreignKey: {
      referencedTable: "contacts",
      referencedColumn: "id",
      constraintName: "contact_tasks_contact_id_fkey",
      onDelete: "CASCADE",
      onUpdate: "NO ACTION",
      relationKind: "many-to-one",
    },
    placeholder: "Search for contact (optional)...",
    formSpan: 3,
    clientSelectConfig: {
      showModal: true,
      limit: 20,
    },
    sortable: true,
    filterable: true,
    searchable: true,
    resizable: true,
    hideable: true,
    editable: true,
    filterType: "client-select",
    width: 200,
    minWidth: 150,
    maxWidth: 300,
    icon: "User",
    groupable: true,
    allowedRoles: ["wtc_employee", "wtc_admin"],
    showOnCard: true,
    cardSpan: 3,
    mobileDetailSectionId: "contact",
  },
  // FIELD: company_name
  company_name: {
    type: "text",
    label: "Company",
    placeholder: "Company name",
    formOnly: true,
    excludeFromApi: true,
    sortable: false,
    filterable: false,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: false,
    width: 200,
    minWidth: 150,
    maxWidth: 300,
    mobileDetailSectionId: "contact",
  },
  // FIELD: company_id
  company_id: {
    type: "company-select",
    label: "Company",
    isForeignKey: true,
    foreignKey: {
      referencedTable: "companies",
      referencedColumn: "id",
      constraintName: "contact_tasks_company_id_fkey",
      onDelete: "SET NULL",
      onUpdate: "NO ACTION",
      relationKind: "many-to-one",
    },
    placeholder: "Search for company (for account-level tasks without a contact)",
    formSpan: 3,
    excludeFromApi: false,
    sortable: true,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: true,
    filterType: "company-select",
    width: 200,
    minWidth: 150,
    maxWidth: 300,
    icon: "Building",
    allowedRoles: ["wtc_employee", "wtc_admin"],
    mobileDetailSectionId: "contact",
  },
  // FIELD: entity_type
  entity_type: {
    type: "text",
    label: "Entity Type",
    placeholder: "Entity type (contacts, deals, companies, etc.)",
    excludeFromApi: false,
    sortable: true,
    filterable: true,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: false,
    width: 150,
    minWidth: 100,
    maxWidth: 200,
    mobileDetailSectionId: "metadata",
  },
  // FIELD: entity_id
  entity_id: {
    type: "text",
    label: "Entity ID",
    placeholder: "UUID of linked entity",
    excludeFromApi: false,
    sortable: true,
    filterable: false,
    searchable: false,
    resizable: true,
    hideable: true,
    editable: false,
    width: 200,
    minWidth: 150,
    maxWidth: 300,
    mobileDetailSectionId: "metadata",
  },
});
